/**
 * Servicio para la gestión de plantillas de contratos
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import type {
  ContractTemplate,
  ContractTemplateFilters,
  CreateContractTemplateDto,
  UpdateContractTemplateDto,
} from '@/types/employee.types';

/**
 * Servicio de plantillas de contratos con todos los endpoints disponibles
 */
export const contractTemplatesService = {
  /**
   * Listar plantillas de contratos con filtros opcionales
   */
  getAll: async (filters?: ContractTemplateFilters): Promise<ContractTemplate[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString();
    const url = queryString
      ? `${API_URL}/contract-templates?${queryString}`
      : `${API_URL}/contract-templates`;
    return apiRequest<ContractTemplate[]>(url);
  },

  /**
   * Obtener plantilla por ID
   */
  getById: async (id: number): Promise<ContractTemplate> => {
    return apiRequest<ContractTemplate>(`${API_URL}/contract-templates/${id}`);
  },

  /**
   * Crear nueva plantilla de contrato
   */
  create: async (data: CreateContractTemplateDto): Promise<ContractTemplate> => {
    return apiRequest<ContractTemplate>(`${API_URL}/contract-templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Actualizar plantilla de contrato
   */
  update: async (
    id: number,
    data: UpdateContractTemplateDto
  ): Promise<ContractTemplate> => {
    return apiRequest<ContractTemplate>(`${API_URL}/contract-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Activar plantilla de contrato
   */
  activate: async (id: number): Promise<ContractTemplate> => {
    return apiRequest<ContractTemplate>(`${API_URL}/contract-templates/${id}/activate`, {
      method: 'POST',
    });
  },

  /**
   * Desactivar plantilla de contrato
   */
  deactivate: async (id: number): Promise<ContractTemplate> => {
    return apiRequest<ContractTemplate>(`${API_URL}/contract-templates/${id}/deactivate`, {
      method: 'POST',
    });
  },
};
